import React, { useState, useEffect } from 'react';
import { string, func, object } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import { addToCompare, removeFromCompare, isInCompare } from './comparisonUtils';
import css from './CompareButton.module.css';

/**
 * CompareButton — adds/removes a listing from the comparison set.
 *
 * Usage (in ListingCard):
 *   {features.listingComparisons && (
 *     <CompareButton listing={{ id, title, price, image }} onChange={set => setCompareSet(set)} />
 *   )}
 *
 * @param {{ id, title, price, image }} props.listing
 * @param {Function} [props.onChange] — called with updated comparison set
 */
const CompareButton = ({ listing, onChange, className }) => {
  const [inSet, setInSet] = useState(() => isInCompare(listing.id));
  const [full, setFull] = useState(false);

  useEffect(() => {
    setInSet(isInCompare(listing.id));
  }, [listing.id]);

  const handleClick = () => {
    if (inSet) {
      const updated = removeFromCompare(listing.id);
      setInSet(false);
      setFull(false);
      onChange && onChange(updated);
    } else {
      const { added, set, full: isFull } = addToCompare(listing);
      if (added) setInSet(true);
      setFull(isFull);
      onChange && onChange(set);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={!inSet && full}
      className={classNames(css.button, inSet && css.active, className)}
      title={full && !inSet ? 'Max 3 listings to compare' : undefined}
    >
      {inSet
        ? <FormattedMessage id="CompareButton.remove" />
        : full
          ? <FormattedMessage id="CompareButton.full" />
          : <FormattedMessage id="CompareButton.add" />}
    </button>
  );
};

CompareButton.defaultProps = { onChange: null, className: null };
CompareButton.propTypes = { listing: object.isRequired, onChange: func, className: string };

export default CompareButton;
